import { z } from 'zod'

import { apiSlice } from '@/shared/api/apiSlice'
import { ServerResponse } from '@/shared/types/common'

import { clearBasketSuccess } from '../model/cart-slice'
import { BonusData, GetBonusDataParams } from '../types/bonus'
import { SetCartInfoProps, TCartItem } from '../types/cart-item'

import { parseSpecialPromotion, SpecialPromotion } from './special-promotion'

type GetProductGiftParams = {
	productId: number
	quantity?: number
}

type CartItem = {
	PRODUCT_ID: number
	QUANTITY: number
}

type GetProductSpecialPromotionParams = {
	productId: number
}

const GiftSchema = z.object({
	ID: z.number(),
	KOD: z.string(),
	TITLE: z.string(),
	PRODUCT_IDS: z.array(z.number()),
	IMAGE: z.string()
})

const GiftResponseSchema = z.object({
	one: z.array(GiftSchema),
	all: z.array(z.array(GiftSchema))
})

export type GiftResponse = z.infer<typeof GiftResponseSchema>

export const cartApi = apiSlice.injectEndpoints({
	endpoints: build => ({
		getCartItems: build.query<TCartItem[], void>({
			query: () => {
				return {
					url: 'basket'
				}
			},
			providesTags: ['Cart'],
			transformResponse: (res: ServerResponse<SetCartInfoProps>) => {
				if (res?.data) {
					return res.data.PRODUCTS
				}

				return null
			}
		}),
		getCartInfo: build.query<SetCartInfoProps | null, void>({
			query: () => {
				return {
					url: 'basket'
				}
			},
			providesTags: ['Cart'],
			transformResponse: (res: ServerResponse<SetCartInfoProps>) => {
				if (res?.data) {
					return {
						...res.data,
						PRODUCTS: res.data.PRODUCTS.map(item => {
							item.RELATED_PRODUCTS = Number(item.RELATED_PRODUCTS)
							return item
						})
					}
				}

				return null
			}
		}),
		getProductGifts: build.query<GiftResponse, GetProductGiftParams>({
			query: data => {
				const { productId, quantity = 1 } = data

				return {
					url: `gift/${productId}`,
					params: {
						QUANTITY: quantity
					}
				}
			},
			transformResponse: (res: ServerResponse<GiftResponse>) => {
				const parsed = GiftResponseSchema.safeParse(res?.data)

				if (parsed.success) return res.data
				else return { one: [], all: [] }
			}
		}),
		getGiftsForManyProducts: build.query<GiftResponse, { products: CartItem[] }>({
			query: data => {
				return {
					url: `/gift?params=${JSON.stringify(data)}`
				}
			},
			transformResponse: (res: ServerResponse<GiftResponse>) => {
				const parsed = GiftResponseSchema.safeParse(res?.data)

				if (parsed.success) return res.data
				else return { one: [], all: [] }
			}
		}),
		getProductSpecialPromotion: build.query<SpecialPromotion | null, GetProductSpecialPromotionParams>({
			query: ({ productId }) => ({
				url: `products/${productId}/special-promotion`
			}),
			transformResponse: (res: ServerResponse<unknown>) => parseSpecialPromotion(res?.data)
		}),
		getBonusData: build.mutation<BonusData, GetBonusDataParams>({
			query: ({
				PHONE = null,
				DELIVERY_ID = null,
				PAY_ID = null,
				PRODUCTS = null,
				USER_PAYMENT_BONUS = 0,
				STORE_ID = null,
				isFastOrder = false,
				coupon
			}) => {
				return {
					url: isFastOrder ? 'fast/order' : 'order',
					method: 'POST',
					params: {
						...(PHONE && { PHONE }),
						...(DELIVERY_ID && { DELIVERY_ID }),
						...(PAY_ID && { PAY_ID }),
						...(PRODUCTS && { PRODUCTS }),
						...(STORE_ID && { STORE_ID }),
						USER_PAYMENT_BONUS
					},
					headers: {
						...(coupon && { Cookie: JSON.stringify({ COUPON: coupon }) })
					}
				}
			},
			transformResponse: (res: ServerResponse<BonusData>) => {
				return res?.data || null
			}
		}),
		removeCartItem: build.mutation<void, { ID: number }>({
			query: ({ ID }) => ({
				url: `basket/${ID}`,
				method: 'DELETE'
			}),
			invalidatesTags: ['Cart']
		}),
		updateCartItemQuantity: build.mutation<void, { ID: number; quantity: number }>({
			query: ({ ID, quantity }) => ({
				url: `basket/${ID}`,
				method: 'PUT',
				params: { quantity }
			}),
			invalidatesTags: ['Cart']
		}),
		addCartItem: build.mutation<
			any,
			{
				productId: number
				quantity?: number
				relatedProduct?: number
				typeProduct?: string
			}
		>({
			query: ({ productId, quantity, relatedProduct, typeProduct }) => ({
				url: `basket/${productId}`,
				method: 'POST',
				params: {
					...(quantity !== undefined && { quantity }),
					...(relatedProduct !== undefined && { relatedProduct }),
					...(typeProduct && { typeProduct })
				}
			}),
			invalidatesTags: ['Cart']
		}),
		clearCart: build.query<void, void>({
			query: () => ({
				url: 'basket/clear'
			}),
			onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled
					dispatch(clearBasketSuccess())
					dispatch(apiSlice.util.invalidateTags(['Cart']))
				} catch {
					// Request errors are handled by RTK Query and the caller hook.
				}
			}
		}),
		addCartItems: build.mutation<void, { products: any[] }>({
			query: ({ products }) => ({
				url: 'basket',
				method: 'POST',
				params: {
					products: JSON.stringify(products)
				}
			}),
			invalidatesTags: ['Cart']
		})
	})
})

export const {
	useGetCartItemsQuery,
	useGetCartInfoQuery,
	useGetProductGiftsQuery,
	useGetProductSpecialPromotionQuery,
	useLazyGetProductGiftsQuery,
	useLazyGetGiftsForManyProductsQuery,
	useGetBonusDataMutation,
	useRemoveCartItemMutation,
	useUpdateCartItemQuantityMutation,
	useAddCartItemMutation,
	useAddCartItemsMutation,
	useLazyClearCartQuery,
	useLazyGetCartInfoQuery
} = cartApi
